import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import {
  GlobalSearchParams,
  GlobalSearchResponse,
  SearchableEntityType,
} from '../../../modules/global-search/globalSearch.types';
import { getGlobalSearchKey } from './globalSearch.keys';
import { findGlobalSearch } from './globalSearch.repository';

interface MergeRetriedGlobalSearchDataParams {
  current: GlobalSearchResponse | undefined;
  retriedData: GlobalSearchResponse;
  entityTypes: SearchableEntityType[];
}

const mergeRetriedGlobalSearchData = ({
  current,
  retriedData,
  entityTypes,
}: MergeRetriedGlobalSearchDataParams): GlobalSearchResponse => {
  if (!current) {
    return retriedData;
  }

  const retriedEntityTypes = new Set(entityTypes);

  return {
    groups: [
      ...current.groups.filter((group) => !retriedEntityTypes.has(group.entityType)),
      ...retriedData.groups,
    ],
    warnings: [
      ...current.warnings.filter(
        (warning) => !warning.entityType || !retriedEntityTypes.has(warning.entityType),
      ),
      ...retriedData.warnings,
    ],
  };
};

export const useRetryGlobalSearchEntities = (mainParams: GlobalSearchParams) => {
  const { team, brandId } = useApplicationContext();
  const queryClient = useQueryClient();
  const mainQueryKey = [team?.id, ...getGlobalSearchKey(mainParams, brandId)];

  const { mutate, isPending } = useMutation({
    mutationFn: async (entityTypes: SearchableEntityType[]) => {
      const response = await findGlobalSearch({ ...mainParams, entityTypes });
      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error('failed to retry global search entities');
      }
      return response.data;
    },
    onSuccess: (retriedData, entityTypes) => {
      queryClient.setQueryData<GlobalSearchResponse>(mainQueryKey, (current) =>
        mergeRetriedGlobalSearchData({ current, retriedData, entityTypes }),
      );
    },
  });

  return {
    retryEntities: mutate,
    isRetrying: isPending,
  };
};
